import { useState, useEffect, useCallback } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Shield, Users, Film, MessageSquare, Search, Trash2, Edit, Loader2, AlertTriangle, History } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { useTranslation } from "../i18n";

const API = '/api';

export function AdminPanel({ user }) {
  const { t } = useTranslation();
  const [tab, setTab] = useState("users");
  const [stats, setStats] = useState(null);
  const [quota, setQuota] = useState(null);

  useEffect(() => {
    axios.get(`${API}/admin/stats`).then(r => setStats(r.data)).catch(() => {});
    axios.get(`${API}/admin/google-books-quota`).then(r => setQuota(r.data)).catch(() => {});
  }, []);

  if (user?.role !== "admin") {
    return (
      <div className="text-center py-16" data-testid="admin-forbidden">
        <Shield className="w-12 h-12 mx-auto mb-4 text-destructive" />
        <h2 className="text-lg font-bold">Acceso denegado</h2>
        <p className="text-sm text-muted-foreground mt-2">No tienes permisos de administrador.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-panel">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold tracking-tight flex items-center gap-3">
          <Shield className="w-7 h-7 text-primary" />
          Admin Panel
        </h1>
        <p className="text-sm text-muted-foreground mt-1">Content management & moderation</p>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="admin-stats">
          <Card><CardContent className="p-4 text-center"><p className="text-2xl font-bold">{stats.total_users}</p><p className="text-xs text-muted-foreground">Users</p></CardContent></Card>
          <Card><CardContent className="p-4 text-center"><p className="text-2xl font-bold">{stats.total_items}</p><p className="text-xs text-muted-foreground">Content</p></CardContent></Card>
          <Card><CardContent className="p-4 text-center"><p className="text-2xl font-bold">{stats.total_entries}</p><p className="text-xs text-muted-foreground">Library entries</p></CardContent></Card>
          <Card><CardContent className="p-4 text-center"><p className="text-2xl font-bold">{stats.total_reviews}</p><p className="text-xs text-muted-foreground">Reviews</p></CardContent></Card>
        </div>
      )}

      {/* Google Books Quota */}
      {quota && (
        <Card data-testid="admin-google-quota">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium">Google Books API</span>
              <span className="text-xs text-muted-foreground">{quota.date}</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex-1">
                <div className="h-2 bg-muted rounded-full overflow-hidden">
                  <div className="h-full bg-primary rounded-full transition-all" style={{ width: `${Math.min(100, (quota.used / quota.limit) * 100)}%` }} />
                </div>
              </div>
              <span className="text-sm font-mono">{quota.used} / {quota.limit}</span>
            </div>
            <p className="text-xs text-muted-foreground mt-1">{quota.remaining} remaining today</p>
          </CardContent>
        </Card>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="w-full grid grid-cols-4">
          <TabsTrigger value="users" data-testid="admin-tab-users"><Users className="w-4 h-4 mr-1.5" />Users</TabsTrigger>
          <TabsTrigger value="content" data-testid="admin-tab-content"><Film className="w-4 h-4 mr-1.5" />Content</TabsTrigger>
          <TabsTrigger value="reviews" data-testid="admin-tab-reviews"><MessageSquare className="w-4 h-4 mr-1.5" />Reviews</TabsTrigger>
          <TabsTrigger value="audit" data-testid="admin-tab-audit"><History className="w-4 h-4 mr-1.5" />Audit</TabsTrigger>
        </TabsList>
        <TabsContent value="users"><UsersTab /></TabsContent>
        <TabsContent value="content"><ContentTab /></TabsContent>
        <TabsContent value="reviews"><ReviewsTab /></TabsContent>
        <TabsContent value="audit"><AuditTab /></TabsContent>
      </Tabs>
    </div>
  );
}

/* ─── Users Tab ─── */
function UsersTab() {
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetch = useCallback(async () => {
    setLoading(true);
    try { const r = await axios.get(`${API}/admin/users?q=${q}&limit=50`); setUsers(r.data.users); }
    catch { toast.error("Error loading users"); }
    finally { setLoading(false); }
  }, [q]);

  useEffect(() => { const t = setTimeout(fetch, 300); return () => clearTimeout(t); }, [fetch]);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/admin/users/${deleteTarget.user_id}`);
      toast.success("User anonymized");
      setDeleteTarget(null);
      fetch();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setDeleting(false); }
  };

  return (
    <div className="space-y-4 mt-4">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input placeholder="Search users..." value={q} onChange={e => setQ(e.target.value)} className="pl-9" data-testid="admin-users-search" />
      </div>
      {loading ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : (
        <div className="space-y-1">
          {users.map(u => (
            <div key={u.user_id} className="flex items-center gap-3 p-3 rounded-lg border border-border bg-card" data-testid={`admin-user-${u.user_id}`}>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">@{u.username} {u.role === "admin" && <span className="text-xs text-primary ml-1">(admin)</span>}</p>
                <p className="text-xs text-muted-foreground truncate">{u.email} · {u.name}</p>
              </div>
              {u.role !== "admin" && !u.deleted && (
                <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive" onClick={() => setDeleteTarget(u)} data-testid={`admin-delete-user-${u.user_id}`}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              )}
              {u.deleted && <span className="text-xs text-muted-foreground">Deleted</span>}
            </div>
          ))}
        </div>
      )}
      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        loading={deleting}
        title="Delete user"
        description={`This will anonymize @${deleteTarget?.username}. Reviews will be removed and the account will be deactivated. This action cannot be undone.`}
      />
    </div>
  );
}

/* ─── Content Tab ─── */
function ContentTab() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [editItem, setEditItem] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetch = useCallback(async () => {
    setLoading(true);
    try { const r = await axios.get(`${API}/admin/content?q=${q}&limit=50`); setItems(r.data.items); }
    catch { toast.error("Error loading content"); }
    finally { setLoading(false); }
  }, [q]);

  useEffect(() => { const t = setTimeout(fetch, 300); return () => clearTimeout(t); }, [fetch]);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/admin/content/${deleteTarget.item_id}`);
      toast.success("Content soft deleted");
      setDeleteTarget(null);
      fetch();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setDeleting(false); }
  };

  return (
    <div className="space-y-4 mt-4">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input placeholder="Search content..." value={q} onChange={e => setQ(e.target.value)} className="pl-9" data-testid="admin-content-search" />
      </div>
      {loading ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : (
        <div className="space-y-1">
          {items.map(item => (
            <div key={item.item_id} className="flex items-center gap-3 p-3 rounded-lg border border-border bg-card" data-testid={`admin-item-${item.item_id}`}>
              <div className="w-10 h-14 flex-shrink-0 rounded overflow-hidden bg-muted">
                {(item.cover_image || item.poster_url) ? <img src={item.cover_image || item.poster_url} alt="" className="w-full h-full object-cover" /> : null}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{item.title}</p>
                <p className="text-xs text-muted-foreground">{item.media_type} · {item.release_year || "N/A"}</p>
              </div>
              <div className="flex gap-1">
                <Button variant="ghost" size="sm" onClick={() => setEditItem(item)} data-testid={`admin-edit-item-${item.item_id}`}><Edit className="w-4 h-4" /></Button>
                <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive" onClick={() => setDeleteTarget(item)} data-testid={`admin-delete-item-${item.item_id}`}><Trash2 className="w-4 h-4" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}
      {editItem && <EditContentDialog item={editItem} onClose={() => setEditItem(null)} onSaved={() => { setEditItem(null); fetch(); }} />}
      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        loading={deleting}
        title="Delete content"
        description={`Are you sure you want to delete "${deleteTarget?.title}"? This will soft-delete the content.`}
      />
    </div>
  );
}

/* ─── Edit Content Dialog ─── */
function EditContentDialog({ item, onClose, onSaved }) {
  const [title, setTitle] = useState(item.title || "");
  const [description, setDescription] = useState(item.description || "");
  const [coverImage, setCoverImage] = useState(item.cover_image || item.poster_url || "");
  const [releaseYear, setReleaseYear] = useState(item.release_year || "");
  const [creator, setCreator] = useState(item.creator || "");
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/content/${item.item_id}`, { title, description, cover_image: coverImage, release_year: releaseYear, creator });
      toast.success("Content updated");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg" data-testid="admin-edit-content-dialog">
        <DialogHeader><DialogTitle>Edit content</DialogTitle><DialogDescription>{item.title}</DialogDescription></DialogHeader>
        <div className="space-y-4">
          <div><Label>Title</Label><Input value={title} onChange={e => setTitle(e.target.value)} /></div>
          <div><Label>Description</Label><Textarea value={description} onChange={e => setDescription(e.target.value)} rows={3} /></div>
          <div><Label>Cover image URL</Label><Input value={coverImage} onChange={e => setCoverImage(e.target.value)} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Release year</Label><Input value={releaseYear} onChange={e => setReleaseYear(e.target.value)} /></div>
            <div><Label>Creator</Label><Input value={creator} onChange={e => setCreator(e.target.value)} /></div>
          </div>
          <div className="flex gap-3 justify-end">
            <Button variant="ghost" onClick={onClose}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving}>{saving ? "Saving..." : "Save changes"}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ─── Reviews Tab ─── */
function ReviewsTab() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetch = useCallback(async () => {
    setLoading(true);
    try { const r = await axios.get(`${API}/admin/reviews?limit=50`); setReviews(r.data.reviews); }
    catch { toast.error("Error loading reviews"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/admin/reviews/${deleteTarget.entry_id}`);
      toast.success("Review deleted");
      setDeleteTarget(null);
      fetch();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setDeleting(false); }
  };

  return (
    <div className="space-y-4 mt-4">
      {loading ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : reviews.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-8">No reviews found.</p>
      ) : (
        <div className="space-y-2">
          {reviews.map(r => (
            <div key={r.entry_id} className="p-3 rounded-lg border border-border bg-card" data-testid={`admin-review-${r.entry_id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-muted-foreground">@{r.username} · {r.item_title}</p>
                  {r.rating && <p className="text-xs mt-0.5">★ {r.rating}</p>}
                  <p className="text-sm mt-1 line-clamp-3">{r.review}</p>
                </div>
                <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive flex-shrink-0" onClick={() => setDeleteTarget(r)} data-testid={`admin-delete-review-${r.entry_id}`}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        loading={deleting}
        title="Delete review"
        description="Are you sure you want to delete this review? This cannot be undone."
      />
    </div>
  );
}

/* ─── Audit Tab ─── */
function AuditTab() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/admin/audit?limit=50`).then(r => setLogs(r.data.logs)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const actionColors = { edit_content: "text-blue-500", delete_content: "text-red-500", delete_user: "text-red-500", delete_review: "text-orange-500" };

  return (
    <div className="space-y-2 mt-4">
      {loading ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : logs.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-8">No actions logged yet.</p>
      ) : logs.map(log => (
        <div key={log.action_id} className="flex items-center gap-3 p-3 rounded-lg border border-border bg-card text-sm" data-testid={`admin-log-${log.action_id}`}>
          <span className={`font-mono text-xs ${actionColors[log.action_type] || "text-muted-foreground"}`}>{log.action_type}</span>
          <span className="flex-1 text-muted-foreground truncate">{log.details}</span>
          <span className="text-xs text-muted-foreground flex-shrink-0">{new Date(log.timestamp).toLocaleString()}</span>
        </div>
      ))}
    </div>
  );
}

/* ─── Confirm Dialog ─── */
function ConfirmDialog({ open, onClose, onConfirm, loading, title, description }) {
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-sm" data-testid="admin-confirm-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-destructive" />{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <div className="flex gap-3 justify-end mt-2">
          <Button variant="ghost" onClick={onClose} disabled={loading}>Cancel</Button>
          <Button variant="destructive" onClick={onConfirm} disabled={loading} data-testid="admin-confirm-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Confirm"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default AdminPanel;
